import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { Input } from "../../components/ui/input";
import { Search, FileImage, Loader2, ExternalLink, Trash2, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_LABELS = {
  drivers_license_front: "Driver's License — Front",
  drivers_license_back: "Driver's License — Back",
  license_front: "Driver's License — Front",
  license_back: "Driver's License — Back",
  insurance: "Insurance Card",
  insurance_card: "Insurance Card",
  finance_contract: "Finance Contract",
  bill_of_sale: "Bill of Sale",
  other: "Other",
};

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [customers, setCustomers] = useState({}); // id → customer
  const [thumbs, setThumbs] = useState({}); // doc.id → dataUrl (cached)
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get("/documents"),
        api.get("/customers"),
      ]);
      setDocs(d || []);
      const cmap = {};
      (c || []).forEach((x) => (cmap[x.id] = x));
      setCustomers(cmap);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Lazy-load thumbnails for visible image docs. Use a ref to track already-
  // fetched ids so setThumbs doesn't retrigger the effect (which would fire
  // O(n^2) requests, saturating the browser's 6-connection pool).
  const loadedIds = useRef(new Set());
  useEffect(() => {
    let cancelled = false;
    const needed = docs.filter(
      (d) =>
        !loadedIds.current.has(d.id) &&
        (d.mime_type || "").startsWith("image/")
    );
    if (needed.length === 0) return;
    needed.forEach((d) => loadedIds.current.add(d.id));
    const run = async () => {
      for (let i = 0; i < needed.length && !cancelled; i += 4) {
        const batch = needed.slice(i, i + 4);
        await Promise.all(
          batch.map(async (d) => {
            try {
              const { data } = await api.get(`/documents/${d.id}`);
              if (!cancelled && data.content_base64) {
                setThumbs((t) => ({ ...t, [d.id]: data.content_base64 }));
              }
            } catch {
              /* silent — icon placeholder stays */
            }
          })
        );
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [docs]);

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      if (typeFilter !== "all") {
        const t = d.doc_type || "";
        // Group front + back license under one filter
        if (typeFilter === "drivers_license") {
          if (!t.startsWith("drivers_license") && !t.startsWith("license_")) return false;
        } else if (typeFilter === "insurance") {
          if (!t.startsWith("insurance")) return false;
        } else if (t !== typeFilter) return false;
      }
      if (q) {
        const needle = q.toLowerCase();
        const c = customers[d.customer_id];
        const hay = [
          c?.full_name,
          c?.email,
          c?.phone,
          c?.drivers_license,
          d.filename,
          d.doc_type,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [docs, customers, q, typeFilter]);

  const openDoc = async (doc) => {
    setPreview({ doc, dataUrl: "" });
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      setPreview({ doc, dataUrl: data.content_base64 });
    } catch {
      toast.error("Could not open document");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Soft-delete this document? Staff can no longer view it.")) return;
    try {
      await api.delete(`/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      if (preview?.doc?.id === id) setPreview(null);
      toast.success("Document deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = useMemo(() => {
    const c = { all: docs.length, drivers_license: 0, insurance: 0, other: 0 };
    docs.forEach((d) => {
      const t = d.doc_type || "";
      if (t.startsWith("drivers_license") || t.startsWith("license_")) c.drivers_license += 1;
      else if (t.startsWith("insurance")) c.insurance += 1;
      else c.other += 1;
    });
    return c;
  }, [docs]);

  const FILTERS = [
    { v: "all", t: "All", n: counts.all },
    { v: "drivers_license", t: "Driver's License", n: counts.drivers_license },
    { v: "insurance", t: "Insurance Card", n: counts.insurance },
    { v: "other", t: "Other", n: counts.other },
  ];

  return (
    <AdminShell title="Documents">
      <div className="mb-4">
        <p className="text-sm text-slate-400 max-w-2xl">
          <FolderOpen className="w-4 h-4 inline mr-1 text-[#D4AF37]" />
          Every driver&apos;s license and insurance card captured through the kiosk or admin scan is
          stored here. Click any file to view.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
          <Input
            placeholder="Search by customer, filename, license #…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="bg-[#04060d] border-white/10 h-10 pl-9"
            data-testid="documents-search"
          />
        </div>
        <div className="flex flex-wrap gap-1" data-testid="documents-filter-bar">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              onClick={() => setTypeFilter(f.v)}
              className={`px-3 py-2 rounded-sm text-xs border transition ${
                typeFilter === f.v
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                  : "border-white/10 text-slate-300 hover:border-white/30"
              }`}
              data-testid={`docs-filter-${f.v}`}
            >
              {f.t} <span className="text-slate-500 ml-1">· {f.n}</span>
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading documents…</div>}

      {!loading && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
          data-testid="documents-grid"
        >
          {filtered.map((d) => {
            const c = customers[d.customer_id];
            const label = DOC_TYPE_LABELS[d.doc_type] || d.doc_type;
            return (
              <div
                key={d.id}
                className="surface rounded-sm p-3 hover:border-[#D4AF37]/40 transition group"
                data-testid={`doc-card-${d.id}`}
              >
                <button
                  onClick={() => openDoc(d)}
                  className="w-full aspect-[3/2] rounded-sm bg-[#04060d] border border-white/10 group-hover:border-[#D4AF37]/30 flex items-center justify-center mb-2 transition overflow-hidden"
                  data-testid={`doc-thumb-${d.id}`}
                >
                  {thumbs[d.id] ? (
                    <img
                      src={thumbs[d.id]}
                      alt={d.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <FileImage className="w-8 h-8 text-[#D4AF37]" />
                  )}
                </button>
                <div className="text-[10px] text-[#D4AF37] uppercase tracking-wider truncate">
                  {label}
                </div>
                <div className="text-sm text-white truncate mt-0.5">
                  {c ? (
                    <Link
                      to={`/admin/customers/${d.customer_id}`}
                      className="hover:underline"
                      data-testid={`doc-customer-${d.id}`}
                    >
                      {c.full_name || "—"}
                    </Link>
                  ) : (
                    <span className="text-slate-500">Unknown customer</span>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 truncate">{d.filename}</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {d.created_at?.slice(0, 10)} · {(d.size / 1024).toFixed(1)} KB
                </div>
                <div className="flex gap-2 mt-2 pt-2 border-t border-white/5">
                  <button
                    onClick={() => openDoc(d)}
                    className="text-[10px] text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                    data-testid={`doc-view-${d.id}`}
                  >
                    <ExternalLink className="w-3 h-3" /> View
                  </button>
                  <button
                    onClick={() => deleteDoc(d.id)}
                    className="text-[10px] text-red-400 hover:underline inline-flex items-center gap-1 ml-auto"
                    data-testid={`doc-delete-${d.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-16">
              No documents match your filters.
            </div>
          )}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="doc-preview-overlay"
        >
          <div
            className="surface rounded-md p-4 max-w-3xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="text-xs text-slate-400 truncate">{preview.doc.filename}</div>
              <button
                onClick={() => setPreview(null)}
                className="text-xs text-slate-400 hover:text-white shrink-0"
                data-testid="doc-preview-close"
              >
                Close
              </button>
            </div>
            {previewLoading ? (
              <div className="text-slate-400 text-sm py-12 text-center inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              <img src={preview.dataUrl} alt={preview.doc.filename} className="max-w-full min-w-[280px] min-h-[280px] object-contain rounded-sm mx-auto" />
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
